// src/services/dataExportService.ts
import { logger } from '../utils/logger';
// GDPR Article 20: Right to Data Portability
// Service to export user data in JSON format

import { supabase } from './supabase';

export interface DataExportResult {
  success: boolean;
  error?: string;
  data?: any;
}

/**
 * Export all user data (GDPR Article 20 compliance)
 * Returns comprehensive JSON export of all personal data
 */
export async function exportUserData(): Promise<DataExportResult> {
  try {
    logger.log('📦 Requesting data export...');

    const { data, error } = await supabase.functions.invoke('export-user-data', {
      method: 'GET',
    });

    if (error) {
      logger.error('Data export error:', error);
      return {
        success: false,
        error: error.message || 'Failed to export data',
      };
    }

    logger.log('✅ Data export successful');
    return {
      success: true,
      data: data,
    };
  } catch (error: any) {
    logger.error('Data export exception:', error);
    return {
      success: false,
      error: error.message || 'Network error during data export',
    };
  }
}

/**
 * Download data export as JSON file
 * Creates a downloadable file with all user data
 */
export async function downloadDataExport(): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await exportUserData();

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'No data returned from export',
      };
    }

    // Create downloadable JSON file
    const jsonString = JSON.stringify(result.data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const filename = `dice-cat-data-export-${date}.json`;

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);

    logger.log(`✅ Downloaded: ${filename}`);
    return { success: true };
  } catch (error: any) {
    logger.error('Download error:', error);
    return {
      success: false,
      error: error.message || 'Failed to download export',
    };
  }
}

/**
 * Get summary of what data will be exported
 * Useful for showing users what's included before downloading
 */
export function getExportDataSummary(): string[] {
  return [
    'Profile Information (username, display name, bio, avatar)',
    'Account Settings (email, created date)',
    'Connections (friends and pending requests)',
    'Blocked Users List',
    'Game Sessions (hosted and participated)',
    'Chat Messages (all your messages)',
    'Chat Memberships',
    'Reports Made',
    'Availability Schedule',
    'Accessibility Preferences',
    'Premium Subscription Status',
    'Minigame Scores',
    'Creature Catcher Progress (if applicable)',
    'Cookie Consent Preferences',
    'Account Deletion Requests (if any)',
  ];
}
